/**
 * ═══════════════════════════════════════════════════════════════════════
 * 🔒🔒🔒  ARCHIVO BLOQUEADO POR ORDEN DEL CREADOR  🔒🔒🔒
 * ═══════════════════════════════════════════════════════════════════════
 * NO MODIFICAR sin autorización explícita del usuario.
 * Para desbloquear, el usuario debe identificarse como creador
 * (ej. "yo soy el creador"). Ver: /app/memory/LOCKED_FILES.md
 * ═══════════════════════════════════════════════════════════════════════
 *
 * 🟥 CUADRO NEGRO 2026-06-03 — Lyon IA (Asistente nativo de DamyxIA).
 *
 * Botón en el header (entre GitHub y el botón azul de conexión) que abre
 * un panel flotante de chat con Lyon, el asistente IA de DamyxIA.
 *
 * Lyon usa gpt-4o-mini vía /api/lyon/chat (Emergent LLM Key). Mantiene
 * historial en memoria del componente; no persiste en MongoDB (la
 * conversación se reinicia al refrescar la página).
 */
import { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { X, Send, Loader2, User, Minus, Copy, Trash2, Check, Layers, Image as ImageIcon, Wand2, AlertTriangle, CheckCircle2, GitBranch, BookmarkPlus, BookOpen, Sparkles, Upload, Store } from 'lucide-react';
import { runGenioCommand } from '../genio/commands';
import { withPremiumGate } from '../genio/premiumGate';
import GenioPremiumGateModal from './GenioPremiumGateModal';
import { BACKEND_URL } from '../config/api';
import { useLanguage } from '../context/LanguageContext';
// 🟦 V11.0 Phase 7 — Bridge para leer el nodo seleccionado del UI Builder.
import {
  subscribeBuilderSelection,
  getCurrentBuilderSelection,
  getBuilderIframe,
  requestApplyInstruction,
} from '../pages/ui_builder/lyonBridge';
import { captureNodeScreenshot } from '../pages/ui_builder/captureNodeScreenshot';
// 🟦 V11.1 — Memoria multi-paso (singleton in-memory).
import {
  rememberSelection,
  rememberPatch,
  rememberUserAction,
  hasImplicitReference,
  getMemorySnapshot,
  subscribeMemory,
} from '../pages/ui_builder/lyonMemory';
// 🟦 V11.3 — Recipe Replay (recipes store + apply).
import {
  addRecipe,
  getRecipes,
  subscribeRecipes,
} from '../pages/ui_builder/recipes/recipesStore';
// 🟦 V11.4 — Style Inference: bridge + modal.
import { inferStyleForNode } from '../pages/ui_builder/styleInference/styleInferenceBridge';
import StyleInferenceModal from '../pages/ui_builder/styleInference/styleInferenceModal';
// 🟦 V11.5 — Recipe Marketplace (panel + UI).
import MarketplacePanel from '../pages/ui_builder/recipesMarketplace/marketplacePanel';
import LyonProyectosPanel from './LyonProyectosPanel';
// 🟦 CUADRO NEGRO — Blindaje "no se rompe nunca" para MarketplacePanel.
import SentryErrorBoundary from './system/SentryErrorBoundary';
// 🟦 V11.x Refactor P1 — sub-componentes y hook extraídos.
import LyonSaveRecipeModal from './lyon/LyonSaveRecipeModal';
import LyonRecipesPanel from './lyon/LyonRecipesPanel';
import { useLyonRecipesActions } from './lyon/useLyonRecipesActions';
// 🟦 V12.0 — Style Packs panel.
import LyonStylePacksPanel from './lyon/LyonStylePacksPanel';
import VerbChipsRow from './lyon/VerbChipsRow';
// 🟦 V14.2 — Refactor (a) seguro: estado de paneles + toolbar de botones.
import { useLyonHeaderState, LyonHeaderToolbar } from './lyon/header';
// 🟦 V14.4 Paso 1 — Hook centralizado del chat de Lyon.
import useLyonChat from './lyon/core/useLyonChat';

const API = `${BACKEND_URL}/api`;

// 🟥 CUADRO NEGRO 2026-06-03 — Constantes del panel flotante movible.
const PANEL_W = 512;   // ancho desktop (sm:max-w-lg => 32rem == 512px)
const PANEL_H = 640;   // alto desktop (sm:h-[640px])
const POSITION_STORAGE_KEY = 'damyxia.lyon.panel.position';
const SIZE_STORAGE_KEY = 'damyxia.lyon.panel.size';

/**
 * GenieIcon — SVG inline de un geniecito de humo/vapor (sin lámpara).
 * Diseño: cabeza con turbante + nudo + cola de humo en espiral debajo.
 * Colores: gradiente cian → violeta para encajar con la paleta.
 */
const GenieIcon = ({ className = '', size = 22 }) => (
  <svg
    className={className}
    width={size}
    height={size}
    viewBox="0 0 32 32"
    fill="none"
    aria-hidden="true"
  >
    <defs>
      <linearGradient id="lyonGenieGrad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#A5F3FC" />
        <stop offset="55%" stopColor="#C4B5FD" />
        <stop offset="100%" stopColor="#F0ABFC" />
      </linearGradient>
    </defs>
    {/* Cola de humo en espiral debajo del genio */}
    <path
      d="M16 28 C 11 26.5, 21 24, 16 22.5 C 11 21, 21 19, 16 17.5"
      stroke="url(#lyonGenieGrad)"
      strokeWidth="1.6"
      strokeLinecap="round"
      fill="none"
      opacity="0.75"
    />
    {/* Cuerpo (forma de humo redondeado) */}
    <path
      d="M10 16.5 C 10 13, 13 11, 16 11 C 19 11, 22 13, 22 16.5 L 22 17 C 22 17.6, 21.4 18, 20.8 18 L 11.2 18 C 10.6 18, 10 17.6, 10 17 Z"
      fill="url(#lyonGenieGrad)"
      opacity="0.95"
    />
    {/* Turbante */}
    <ellipse cx="16" cy="8.5" rx="5.5" ry="3" fill="url(#lyonGenieGrad)" />
    {/* Nudo del turbante */}
    <circle cx="16" cy="5.5" r="1.6" fill="#FDE68A" />
    {/* Ojos */}
    <circle cx="14" cy="14.5" r="0.9" fill="#0F172A" />
    <circle cx="18" cy="14.5" r="0.9" fill="#0F172A" />
    {/* Sonrisa */}
    <path
      d="M14.4 16.2 Q 16 17.3, 17.6 16.2"
      stroke="#0F172A"
      strokeWidth="0.9"
      strokeLinecap="round"
      fill="none"
    />
  </svg>
);

// Burbujas decorativas — paleta azul/celeste/violeta, delays escalonados.
const BUBBLES = [
  { left: '18%', size: 5, delay: '0s',   duration: '2.4s', color: '#67E8F9' },
  { left: '38%', size: 4, delay: '0.5s', duration: '2.8s', color: '#A78BFA' },
  { left: '58%', size: 6, delay: '1s',   duration: '2.2s', color: '#38BDF8' },
  { left: '72%', size: 3, delay: '1.4s', duration: '2.6s', color: '#C4B5FD' },
  { left: '28%', size: 4, delay: '1.8s', duration: '2.5s', color: '#22D3EE' },
];

const WELCOME_BULLETS = {
  es: [
    'Puedes preguntarme cualquier cosa',
    'Yo respondo según el contexto de DamyxIA',
    'Puedo guiarte paso a paso',
    'Puedo explicarte qué hace cada sección',
    'Puedo decirte cómo ejecutar algo',
    'Puedo decirte cómo usar una plantilla',
    'Puedo decirte cómo crear un dashboard',
    'Puedo decirte cómo usar Vision',
    'Puedo decirte cómo exportar a GitHub',
    'Puedes preguntar Ahora tus Dudas',
  ],
  en: [
    'You can ask me anything',
    'I answer based on DamyxIA context',
    'I can guide you step by step',
    'I can explain what each section does',
    'I can tell you how to run something',
    'I can tell you how to use a template',
    'I can tell you how to create a dashboard',
    'I can tell you how to use Vision',
    'I can tell you how to export to GitHub',
    'You can ask your questions now',
  ],
};

const LyonHeaderButton = () => {
  const { language } = useLanguage();
  const es = language === 'es';
  const [open, setOpen] = useState(false);
  // 🟥 CUADRO NEGRO 2026-02 — Animación "genieActivate" del trigger.
  // Cuando se abre el modal, se aplica una clase temporal `.genie-activate`
  // por 260ms que dispara la animación de activación (scale + brightness).
  const [isActivating, setIsActivating] = useState(false);
  // 🟥 CUADRO NEGRO Genio Premium Gate (feb-2026): bloqueo total del
  // Genio para usuarios NO Premium. handleOpen verifica isUserPremium()
  // ANTES de abrir el panel; si no es Premium, abre el modal y NO hace
  // ninguna llamada al backend. Además escuchamos el evento global
  // `genio-premium-gate` para reabrir este modal cuando cualquier
  // endpoint de Lyon devuelva 403 premium_required.
  const [premiumGateOpen, setPremiumGateOpen] = useState(false);
  // 🟦 CUADRO NEGRO: confirmación de cierre de Lyon.
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  // 🟦 CUADRO NEGRO Proyectos del Builder.
  const [proyectosOpen, setProyectosOpen] = useState(false);
  // 🟦 V14.4 Fase 2 — Chat state ahora vive en `useLyonChat` (línea ~556),
  // declarado DESPUÉS de builderSelection/attachContext/attachVision/multiStep
  // para que `handleSend` tenga acceso a ellos en su closure.
  const scrollRef = useRef(null);

  // 🟥 CUADRO NEGRO 2026-06-03 — Panel flotante movible + minimizar.
  // position: {x, y} en píxeles. null = aún no se ha posicionado (se centra).
  // Se persiste en sessionStorage para mantener la posición tras cerrar.
  const [position, setPosition] = useState(() => {
    try {
      const raw = sessionStorage.getItem(POSITION_STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        // En mobile (<640px) ignoramos cualquier posición persistida desde
        // desktop — el panel debe renderizar como bottom sheet (position
        // null → left:0, right:0, bottom:0).
        if (typeof window !== 'undefined' && window.innerWidth < 640) return null;
        return parsed;
      }
    } catch { /* ignore */ }
    return null;
  });
  const [minimized, setMinimized] = useState(false);
  const dragStateRef = useRef({ dragging: false, offsetX: 0, offsetY: 0 });
  const [isDragging, setIsDragging] = useState(false);

  // 🟥 RESIZE — panel redimensionable nativamente desde la esquina (CSS
  // `resize: both`). El usuario puede agrandar/achicar el panel. Un
  // ResizeObserver detecta el cambio y actualiza la CSS var
  // `--genio-scale` para escalar proporcionalmente el contenido interno.
  // Base de diseño: 420×600 → scale=1.0 cuando el panel mide 420×600.
  // El tamaño se persiste en sessionStorage para que sobreviva
  // minimize/restore (NO se mantiene en React state para evitar un loop
  // de feedback ResizeObserver↔setState).
  const panelRef = useRef(null);
  const initialSizeAppliedRef = useRef(false);

  const readPersistedSize = () => {
    // Clamp al viewport actual — evita que un panel persistido desde una
    // sesión desktop (512×640) overflow en mobile (≤640px de ancho).
    const vw = typeof window !== 'undefined' ? window.innerWidth : 1024;
    const vh = typeof window !== 'undefined' ? window.innerHeight : 720;
    const maxW = Math.max(320, Math.floor(vw * 0.9));
    const maxH = Math.max(280, Math.floor(vh * 0.9));
    // 🟥 feedback usuario feb-2026 — el panel arrancaba muy ancho (a veces
    // 1200px+ por sesiones persistidas viejas). Clampamos el ancho INICIAL
    // a un máximo cómodo (700px) — el usuario igual puede agrandarlo con
    // los handles de resize si lo necesita. NO afecta a la altura.
    const initialMaxW = Math.min(maxW, 700);
    // 🟥 feedback usuario jun-2026 — en mobile (vw ≤ 640) el panel arrancaba
    // a 640px de alto dejando un hueco negro grande debajo del welcome.
    // Reducimos el default mobile a 520px (~ alto del contenido welcome) —
    // el usuario puede expandirlo arrastrando los handles N/S/E/W.
    const isMobile = vw <= 640;
    const defaultH = isMobile ? 520 : 640;
    try {
      const raw = sessionStorage.getItem(SIZE_STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && parsed.w > 0 && parsed.h > 0) {
          return { w: Math.min(parsed.w, initialMaxW), h: Math.min(parsed.h, maxH) };
        }
      }
    } catch { /* ignore */ }
    // 🟥 feedback usuario feb-2026 — default narrower (440 vs 512). Cabe
    // ~3 botones visibles del toolbar y el resto via scroll horizontal,
    // según el comportamiento solicitado por el usuario.
    return { w: Math.min(440, initialMaxW), h: Math.min(defaultH, maxH) };
  };

  // 🟥 CUADRO NEGRO 2026-06-03 — Mini-icono flotante arrastrable.
  // Cuando Lyon está minimizado, el geniecito se muestra como FAB
  // arrastrable libremente. Posición inicial: junto al botón del bombillo
  // (data-testid="advanced-assistant-light-toggle") si existe, si no en
  // top:130 right:16. Persistido en sessionStorage.
  const MINI_POS_KEY = 'damyxia.lyon.minibtn.position';
  const [miniPos, setMiniPos] = useState(() => {
    try {
      const raw = sessionStorage.getItem(MINI_POS_KEY);
      if (raw) return JSON.parse(raw);
    } catch { /* ignore */ }
    return null;
  });
  const miniBtnRef = useRef(null);
  const miniDragRef = useRef({ dragging: false, moved: false, offX: 0, offY: 0, startX: 0, startY: 0 });
  const [miniDragging, setMiniDragging] = useState(false);

  // 🟥 Tooltip flotante del botón del header.
  // Se usa `position: fixed` con coordenadas calculadas para evitar que el
  // header padre (que tiene `overflow-hidden`) recorte el tooltip.
  const triggerRef = useRef(null);
  const [tipPos, setTipPos] = useState(null); // {x, y} | null

  const showTip = () => {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    // Posicionamos el tooltip DEBAJO del botón porque el botón vive en la
    // barra superior; si lo pusiéramos arriba, quedaría fuera del viewport.
    setTipPos({ x: r.left + r.width / 2, y: r.bottom + 8 });
  };
  const hideTip = () => setTipPos(null);

  const bullets = es ? WELCOME_BULLETS.es : WELCOME_BULLETS.en;

  // Calcular posición inicial centrada (solo se llama al abrir por primera vez).
  const computeCenteredPosition = () => {
    if (typeof window === 'undefined') return null;
    if (window.innerWidth < 640) return null; // mobile = bottom sheet (left:0,bottom:0)
    return {
      x: Math.max(10, (window.innerWidth - PANEL_W) / 2),
      y: Math.max(10, (window.innerHeight - PANEL_H) / 2),
    };
  };

  const handleOpen = () => {
    // 🟢 CUADRO NEGRO Genio Restore (feb-2026, rev2): el chat conversacional
    // es para TODOS los usuarios autenticados. El gate Premium aplica SOLO a
    // los botones avanzados (recetas, style packs, etc.) y a los endpoints
    // `/api/builder/lyon/*` del backend, NO al acto de abrir el Genio.

    // 🟥 FIX feb-2026 — Toggle robusto:
    //   - Si está abierto Y minimizado → restaurar (quitar minimizado).
    //   - Si está abierto Y visible    → cerrar (toggle real).
    //   - Si está cerrado              → abrir.
    // Esto cierra el bug "una vez abre y otra no" que ocurría cuando el
    // panel quedaba en estado open=true + minimized=true (display:none) y
    // un nuevo click sobre el header no hacía nada porque open ya era true.
    if (open) {
      if (minimized) {
        setMinimized(false);
        setIsActivating(true);
        setTimeout(() => setIsActivating(false), 280);
        return;
      }
      // Está abierto y visible → cerrar.
      setOpen(false);
      return;
    }
    // Estaba cerrado → abrir limpio.
    setMinimized(false);
    if (position === null) {
      const initial = computeCenteredPosition();
      if (initial) setPosition(initial);
    }
    setOpen(true);
    // 🟥 CUADRO NEGRO 2026-02 — disparar animación de activación (260ms).
    setIsActivating(true);
    setTimeout(() => setIsActivating(false), 280);
  };

  // 🟥 Listener global: si algún endpoint AVANZADO devuelve 403 premium_required
  // (typ. /api/builder/lyon/*), mostramos el modal Premium en vez de un
  // toast/banner rojo. El chat (/api/lyon/chat) NO dispara este evento porque
  // ya no devuelve 403 — todos los usuarios pueden conversar.
  useEffect(() => {
    const onGate = () => {
      setPremiumGateOpen(true);
    };
    window.addEventListener('genio-premium-gate', onGate);
    return () => window.removeEventListener('genio-premium-gate', onGate);
  }, []);

  // Persistir posición en sessionStorage cada vez que cambia.
  useEffect(() => {
    if (position) {
      try { sessionStorage.setItem(POSITION_STORAGE_KEY, JSON.stringify(position)); } catch { /* ignore */ }
    }
  }, [position]);

  // 🟦 V12.6 — RESIZE OBSERVER (per feedback usuario: sin gaps verticales):
  // Eliminado el transform-scale y la lógica de --genio-scale. El inner
  // ahora ocupa 100%/100% del panel via flexbox interno (header + flex-1
  // messages + footer). Aquí SÓLO persistimos el tamaño en sessionStorage.
  useEffect(() => {
    if (!open || minimized) return undefined;
    const el = panelRef.current;
    if (!el) return undefined;
    // Aplicar el tamaño persistido SOLO la primera vez por sesión.
    if (!initialSizeAppliedRef.current) {
      const persisted = readPersistedSize();
      el.style.width = `${persisted.w}px`;
      el.style.height = `${persisted.h}px`;
      initialSizeAppliedRef.current = true;
    }
    const update = () => {
      const w = el.clientWidth;
      const h = el.clientHeight;
      if (w <= 0 || h <= 0) return;
      // Persistir tamaño en sessionStorage (NO en React state).
      try {
        sessionStorage.setItem(SIZE_STORAGE_KEY, JSON.stringify({ w, h }));
      } catch { /* ignore */ }
    };
    update();
    let ro = null;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(update);
      ro.observe(el);
    }
    window.addEventListener('resize', update);
    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener('resize', update);
    };
  }, [open, minimized]);

  // 🟥 RESIZE HANDLES — handlers de resize personalizado para 8 direcciones
  // (n, s, e, w, ne, nw, se, sw). Cada handle muta directamente el inline
  // style del panel (DOM) sin React state → cero loops. Para handles del
  // norte/oeste también se ajusta `position` (state) para mantener la
  // esquina opuesta anclada visualmente. Se respetan min/max (320×280 →
  // 90vw×90vh) en cada move event.
  const startResize = (e, dir) => {
    e.preventDefault();
    e.stopPropagation();
    const el = panelRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const startW = rect.width;
    const startH = rect.height;
    const startX = e.clientX;
    const startY = e.clientY;
    const startPos = position
      ? { x: position.x, y: position.y }
      : { x: rect.left, y: rect.top };
    if (!position) setPosition(startPos);
    const minW = 320;
    const minH = 280;
    const maxW = window.innerWidth * 0.9;
    const maxH = window.innerHeight * 0.9;
    const target = e.currentTarget;
    try { target.setPointerCapture(e.pointerId); } catch { /* ignore */ }

    const onMove = (ev) => {
      const dx = ev.clientX - startX;
      const dy = ev.clientY - startY;
      let newW = startW;
      let newH = startH;
      let newPosX = startPos.x;
      let newPosY = startPos.y;

      if (dir.includes('e')) {
        newW = Math.min(maxW, Math.max(minW, startW + dx));
      }
      if (dir.includes('w')) {
        newW = Math.min(maxW, Math.max(minW, startW - dx));
        newPosX = startPos.x + (startW - newW);
      }
      if (dir.includes('s')) {
        newH = Math.min(maxH, Math.max(minH, startH + dy));
      }
      if (dir.includes('n')) {
        newH = Math.min(maxH, Math.max(minH, startH - dy));
        newPosY = startPos.y + (startH - newH);
      }

      // Mutación DIRECTA del DOM — sin React state → sin re-render.
      // El ResizeObserver detecta el cambio y actualiza --genio-scale +
      // sessionStorage automáticamente.
      el.style.width = `${newW}px`;
      el.style.height = `${newH}px`;

      if (newPosX !== startPos.x || newPosY !== startPos.y) {
        setPosition({ x: newPosX, y: newPosY });
      }
    };

    const onUp = () => {
      try { target.releasePointerCapture(e.pointerId); } catch { /* ignore */ }
      target.removeEventListener('pointermove', onMove);
      target.removeEventListener('pointerup', onUp);
      target.removeEventListener('pointercancel', onUp);
    };

    target.addEventListener('pointermove', onMove);
    target.addEventListener('pointerup', onUp);
    target.addEventListener('pointercancel', onUp);
  };

  // Listeners globales de drag (pointer = mouse + touch unificado).
  useEffect(() => {
    if (!isDragging) return;
    const handleMove = (e) => {
      if (!dragStateRef.current.dragging) return;
      const clientX = e.clientX ?? (e.touches && e.touches[0]?.clientX);
      const clientY = e.clientY ?? (e.touches && e.touches[0]?.clientY);
      if (clientX == null || clientY == null) return;
      const newX = clientX - dragStateRef.current.offsetX;
      const newY = clientY - dragStateRef.current.offsetY;
      // Mantener el panel dentro del viewport (margen 8px).
      const maxX = window.innerWidth - 60;
      const maxY = window.innerHeight - 40;
      const clampedX = Math.min(Math.max(-PANEL_W + 80, newX), maxX);
      const clampedY = Math.min(Math.max(0, newY), maxY);
      setPosition({ x: clampedX, y: clampedY });
    };
    const handleUp = () => {
      dragStateRef.current.dragging = false;
      setIsDragging(false);
    };
    window.addEventListener('pointermove', handleMove);
    window.addEventListener('pointerup', handleUp);
    window.addEventListener('pointercancel', handleUp);
    return () => {
      window.removeEventListener('pointermove', handleMove);
      window.removeEventListener('pointerup', handleUp);
      window.removeEventListener('pointercancel', handleUp);
    };
  }, [isDragging]);

  const handleDragStart = (e) => {
    // Solo arrastrar con pointer principal (botón izquierdo del mouse o touch).
    if (e.button != null && e.button !== 0) return;
    // 🟦 V12.5 — usar panelRef (root), no target.parentElement: el header
    // ahora vive dentro de un anchor wrapper centrado por flexbox, así
    // que su parent rect no coincide con el rect del panel.
    const rect = panelRef.current?.getBoundingClientRect();
    if (!rect) return;
    dragStateRef.current = {
      dragging: true,
      offsetX: e.clientX - rect.left,
      offsetY: e.clientY - rect.top,
    };
    setIsDragging(true);
    // Si todavía no hay posición (mobile bottom sheet), inicializarla en
    // la posición actual del rect para que el siguiente move la tome.
    if (position === null) {
      setPosition({ x: rect.left, y: rect.top });
    }
  };

  // 🟦 V14.4 Fase 2 — Auto-scroll movido más abajo, después de useLyonChat
  // (necesita `messages` y `sending` del hook).

  // 🟥 Inicializar posición del mini-icono cuando se minimiza por primera vez.
  // 🟥 FIX feb-2026 — Posicionar SIEMPRE en la esquina inferior-derecha
  // (ubicación universal de widgets de chat), con clamping al viewport.
  // Si había una posición previa de sessionStorage, validamos que esté
  // dentro del viewport visible — si quedó offscreen por cambio de
  // resolución, la corregimos a la esquina inferior-derecha.
  useEffect(() => {
    if (!minimized) return;
    const W = 56;  // tamaño del mini button
    const MARGIN = 24;
    const fallbackX = Math.max(MARGIN, window.innerWidth - W - MARGIN);
    const fallbackY = Math.max(MARGIN, window.innerHeight - W - MARGIN);

    if (miniPos) {
      // Validar que la posición persistida esté visible. Si está fuera del
      // viewport (resolución cambió), corregir.
      const visibleX = miniPos.x >= 0 && miniPos.x <= window.innerWidth - W;
      const visibleY = miniPos.y >= 0 && miniPos.y <= window.innerHeight - W;
      if (!visibleX || !visibleY) {
        setMiniPos({ x: fallbackX, y: fallbackY });
      }
      return;
    }
    // Sin posición previa → esquina inferior derecha (chat-widget standard).
    setMiniPos({ x: fallbackX, y: fallbackY });
  }, [minimized, miniPos]);

  // Persistir posición del mini-icono.
  useEffect(() => {
    if (miniPos) {
      try { sessionStorage.setItem(MINI_POS_KEY, JSON.stringify(miniPos)); } catch { /* ignore */ }
    }
  }, [miniPos]);

  // Listeners globales de drag del mini-icono.
  useEffect(() => {
    if (!miniDragging) return;
    const onMove = (e) => {
      const st = miniDragRef.current;
      if (!st.dragging) return;
      const cx = e.clientX ?? (e.touches && e.touches[0]?.clientX);
      const cy = e.clientY ?? (e.touches && e.touches[0]?.clientY);
      if (cx == null || cy == null) return;
      if (!st.moved && (Math.abs(cx - st.startX) > 4 || Math.abs(cy - st.startY) > 4)) {
        st.moved = true;
      }
      const W = 48;
      const x = Math.min(Math.max(0, cx - st.offX), window.innerWidth - W);
      const y = Math.min(Math.max(0, cy - st.offY), window.innerHeight - W);
      setMiniPos({ x, y });
    };
    const onUp = () => {
      miniDragRef.current.dragging = false;
      setMiniDragging(false);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
  }, [miniDragging]);

  const handleMiniPointerDown = (e) => {
    if (e.button != null && e.button !== 0) return;
    const node = miniBtnRef.current;
    if (!node) return;
    const r = node.getBoundingClientRect();
    miniDragRef.current = {
      dragging: true,
      moved: false,
      offX: e.clientX - r.left,
      offY: e.clientY - r.top,
      startX: e.clientX,
      startY: e.clientY,
    };
    setMiniDragging(true);
  };

  // Click vs Drag: si NO se movió → click → restaurar el panel.
  const handleMiniClick = () => {
    if (miniDragRef.current.moved) {
      // Fue un arrastre, no un click. Resetear flag y NO abrir.
      miniDragRef.current.moved = false;
      return;
    }
    setMinimized(false);
  };

  // 🟦 V11.0 Phase 7 — Integración con el UI Builder.
  // `builderSelection` se actualiza vía bridge cuando el usuario selecciona
  // un nodo en el iframe del Builder. `attachContext` controla si ese
  // contexto se envía a Lyon; `attachVision` agrega screenshot html2canvas.
  // ⚠️ ESTAS DECLARACIONES DEBEN IR ANTES DE `handleSend` porque su
  // dependency array las referencia (TDZ evita const-before-decl).
  const [builderSelection, setBuilderSelection] = useState(() => getCurrentBuilderSelection());
  const [attachContext, setAttachContext] = useState(true);
  const [attachVision, setAttachVision] = useState(false);
  const [capturingShot, setCapturingShot] = useState(false);

  // 🟦 V11.1 — Conversación multi-paso. Toggle UI + snapshot reactivo de la
  // memoria. Si el usuario activa este modo, Lyon usará automáticamente el
  // último nodo recordado cuando detecte una referencia implícita ("ese
  // mismo", "ahora hacelo más grande", "continuá", etc.) sin requerir
  // re-selección manual.
  const [multiStep, setMultiStep] = useState(true);
  const [memory, setMemory] = useState(() => getMemorySnapshot());

  useEffect(() => {
    // Al abrir el panel, leer la selección actual (puede haber sido publicada
    // antes de montar este componente — el bridge guarda el último valor en
    // window.__damyxiaBuilderSelection).
    setBuilderSelection(getCurrentBuilderSelection());
    const unsubSel = subscribeBuilderSelection((sel) => {
      setBuilderSelection(sel);
      // 🟦 V11.1 — cada selección publicada actualiza la memoria. Si es un
      // nodo distinto, rememberSelection limpia el chain previo (los
      // chains son por-nodo). No persistimos screenshot acá: se captura
      // sólo cuando attachVision se activa al mandar mensaje.
      if (sel) rememberSelection(sel, null);
    });
    const unsubMem = subscribeMemory((snap) => setMemory(snap));
    return () => { unsubSel(); unsubMem(); };
  }, []);

  // Cuando deja de haber selección, desactivar vision automáticamente para
  // evitar que el toggle quede activo "huérfano".
  useEffect(() => {
    if (!builderSelection) setAttachVision(false);
  }, [builderSelection]);

  // 🟦 V14.4 Fase 2 — useLyonChat: hook centralizado del chat.
  // Reemplaza messages/input/sending/sessionIdRef/copiedId/applyState + los
  // handlers handleSend/handleCopyMessage/handleDeleteMessage que vivían
  // dispersos en este componente. Mantiene EXACTAMENTE el mismo
  // comportamiento (incluyendo el flujo V11.0 Phase 7 + V11.1 multi-paso).
  const {
    messages, setMessages,
    input, setInput,
    sending,
    sessionIdRef,
    copiedId,
    applyState, setApplyState,
    handleSend,
    handleCopyMessage,
    handleDeleteMessage,
  } = useLyonChat({
    es,
    API,
    builderSelection,
    attachContext,
    attachVision,
    multiStep,
    setCapturingShot,
    getBuilderIframe,
    captureNodeScreenshot,
    rememberUserAction,
    hasImplicitReference,
    getMemorySnapshot,
  });

  // Auto-scroll al fondo cuando llegan mensajes nuevos.
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, sending]);

  // 🟦 V11.4 — Style Inference state + handlers.
  // 🟦 V14.2 — `inferenceModalOpen` ahora vive en useLyonHeaderState; el resto sigue local.
  const [inferenceResult, setInferenceResult] = useState(null);
  const [inferenceLoading, setInferenceLoading] = useState(false);

  const handleStartInference = useCallback(async () => {
    const target = builderSelection || (multiStep ? getMemorySnapshot().lastNodeContext : null);
    if (!target?.uib_id) {
      setMessages((prev) => [...prev, {
        id: `sys-${Date.now()}`, role: 'assistant', system: true,
        content: es ? '⚠️ Seleccioná un nodo primero para inferir su estilo.' : '⚠️ Select a node first to infer its style.',
      }]);
      return;
    }
    setInferenceLoading(true);
    try {
      const r = await inferStyleForNode(target);
      if (!r.ok) {
        setMessages((prev) => [...prev, {
          id: `sys-${Date.now()}`, role: 'assistant', system: true,
          content: `⚠️ ${es ? 'No pude inferir el estilo' : 'Could not infer style'}: ${r.error}`,
        }]);
        return;
      }
      setInferenceResult(r);
      setInferenceModalOpen(true);
    } finally {
      setInferenceLoading(false);
    }
  }, [builderSelection, multiStep, es]);

  // 🟦 V11.3 — Recipe Replay: state local del UI de recetas.
  // 🟦 V14.2 — `saveRecipeModalOpen` y `recipesPanelOpen` movidos a useLyonHeaderState.
  // `appId` se infiere del URL del builder; recetas son scoped por appId.
  const [recipes, setRecipes] = useState([]);
  const [recipeName, setRecipeName] = useState('');
  const [recipeError, setRecipeError] = useState(null);
  const [recipeApplyState, setRecipeApplyState] = useState({}); // {id: 'loading'|'ok'|'err'}

  // 🟦 V11.5 — Marketplace + share + import.
  // 🟦 V14.2 — `marketplaceOpen` movido a useLyonHeaderState.
  const [shareUrlByRecipe, setShareUrlByRecipe] = useState({}); // {id: url}
  const [importBusy, setImportBusy] = useState(false);
  // 🟦 V12.0 — Style Packs panel — `stylePacksOpen` movido a useLyonHeaderState.

  // 🟦 V14.2 — Hook centralizado de paneles secundarios.
  // Expone los setters legacy (`setMarketplaceOpen`, `setRecipesPanelOpen`,
  // `setStylePacksOpen`, `setSaveRecipeModalOpen`, `setInferenceModalOpen`)
  // así el código abajo no requiere cambios de nombre.
  const {
    state: lyonHeaderState,
    setters: lyonHeaderSetters,
  } = useLyonHeaderState();
  const {
    recipesPanelOpen, marketplaceOpen, stylePacksOpen,
    saveRecipeModalOpen, inferenceModalOpen,
  } = lyonHeaderState;
  const {
    setRecipesPanelOpen, setMarketplaceOpen, setStylePacksOpen,
    setSaveRecipeModalOpen, setInferenceModalOpen,
  } = lyonHeaderSetters;

  const currentAppId = (() => {
    if (typeof window === 'undefined') return null;
    const m = window.location.pathname.match(/\/apps\/([^/]+)\/builder/);
    return m ? m[1] : null;
  })();

  useEffect(() => {
    setRecipes(getRecipes(currentAppId));
    const unsub = subscribeRecipes(({ appId, recipes: list }) => {
      if (appId === currentAppId) setRecipes(list);
    });
    return unsub;
  }, [currentAppId]);

  // Receta candidata: ≥2 patches consecutivos sobre el mismo nodo.
  const canSaveRecipe = memory.lastNodeId && memory.lastPatches.length >= 2;

  // 🟦 V11.4 — Handlers que dependen de `currentAppId` (declarado arriba en
  // el bloque V11.3) y de `inferenceResult`. Van AQUÍ para evitar TDZ.
  const handleSaveInferredAsRecipe = useCallback(() => {
    if (!inferenceResult || !currentAppId) return;
    const target = builderSelection || getMemorySnapshot().lastNodeContext;
    const nodeType = (target?.tag || 'div').toLowerCase();
    const saved = addRecipe({
      appId: currentAppId,
      name: inferenceResult.styleName,
      description: inferenceResult.description,
      nodeType,
      steps: [{ instruction: `[inferred] ${inferenceResult.styleName}`, patch: inferenceResult.patches }],
    });
    setInferenceModalOpen(false);
    setInferenceResult(null);
    if (saved) {
      setMessages((prev) => [...prev, {
        id: `sys-${Date.now()}`, role: 'assistant', system: true,
        content: es ? `⭐ Receta creada: "${saved.name}"` : `⭐ Recipe created: "${saved.name}"`,
      }]);
    }
  }, [inferenceResult, currentAppId, builderSelection, es]);

  const handleApplyInferredNow = useCallback(async () => {
    if (!inferenceResult) return;
    const target = builderSelection || getMemorySnapshot().lastNodeContext;
    if (!target?.uib_id) return;
    setInferenceLoading(true);
    try {
      const iframeEl = getBuilderIframe();
      const win = iframeEl?.contentWindow;
      if (win) {
        const changes = {};
        for (const op of inferenceResult.patches) {
          if (!op?.path?.startsWith('/styles/')) continue;
          const key = op.path.slice('/styles/'.length);
          if (!key) continue;
          if (!changes.styles) changes.styles = { ...(target.styles || {}) };
          if (op.op === 'remove') delete changes.styles[key];
          else changes.styles[key] = String(op.value ?? '');
        }
        if (Object.keys(changes).length > 0) {
          win.postMessage({ kind: 'uib-update-node', uib_id: target.uib_id, changes }, '*');
          rememberSelection(target, null);
          rememberUserAction(`[style:${inferenceResult.styleName}]`);
          rememberPatch({
            instruction: `[style:${inferenceResult.styleName}]`,
            patch: inferenceResult.patches,
            updatedNodeContext: { ...target, ...changes, styles: { ...(target.styles || {}), ...(changes.styles || {}) } },
            screenshotB64: null,
          });
        }
      }
      setInferenceModalOpen(false);
      setInferenceResult(null);
      setMessages((prev) => [...prev, {
        id: `sys-${Date.now()}`, role: 'assistant', system: true,
        content: es ? `✨ Estilo aplicado: "${inferenceResult.styleName}"` : `✨ Style applied: "${inferenceResult.styleName}"`,
      }]);
    } finally {
      setInferenceLoading(false);
    }
  }, [inferenceResult, builderSelection, es]);

  const {
    handleOpenSaveRecipe,
    handleConfirmSaveRecipe,
    handleDeleteRecipe,
    handleExportRecipe,
    handleShareRecipe,
    handleImportRecipeFromFile,
    handleApplyRecipe,
  } = useLyonRecipesActions({
    es,
    currentAppId,
    memory,
    canSaveRecipe,
    builderSelection,
    multiStep,
    recipeName,
    importBusy,
    setMessages,
    setSaveRecipeModalOpen,
    setRecipeName,
    setRecipeError,
    setRecipeApplyState,
    setShareUrlByRecipe,
    setImportBusy,
  });

  // ESC cierra los modales V11.3.
  useEffect(() => {
    if (!saveRecipeModalOpen && !recipesPanelOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setSaveRecipeModalOpen(false);
        setRecipesPanelOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [saveRecipeModalOpen, recipesPanelOpen]);

  // 🟦 V14.4 Fase 2 — handleSend / handleCopyMessage / handleDeleteMessage
  // y los useState de copiedId / applyState ahora son entregados por el
  // hook `useLyonChat` (declarado más arriba). Bloque inline eliminado.

  // 🟦 V11.0 Phase 7+ — "Aplicar como patch".
  // Por cada mensaje del asistente, si hay un nodo seleccionado en el
  // Builder, mostramos un botón que pide al UI Builder aplicar la
  // sugerencia como JSON Patch (sin remount). El estado por-mensaje guarda
  // {loading|ok|err, detail?} para feedback visual transitorio.

  const handleApplyAsPatch = useCallback(async (msg) => {
    if (!msg?.id || !msg?.content) return;
    // 🟦 V11.1 — Determinar sobre qué nodo se aplica:
    //   1) Si el mensaje fue generado usando memoria multi-paso, usar
    //      msg.targetNodeContext (override explícito).
    //   2) Si NO hay selección actual pero el multi-step está ON y hay
    //      memoria, usar la memoria también.
    //   3) Caso normal: dejar que UiBuilderPage use `selected` (override=null).
    let overrideNode = null;
    if (msg.usedMemory && msg.targetNodeContext) {
      overrideNode = msg.targetNodeContext;
    } else if (!builderSelection && multiStep) {
      const snap = getMemorySnapshot();
      if (snap.lastNodeContext) overrideNode = snap.lastNodeContext;
    }
    // Si NO tenemos ni selección ni memoria, no hay sobre qué aplicar.
    if (!builderSelection && !overrideNode) {
      setApplyState((prev) => ({ ...prev, [msg.id]: { status: 'err', detail: 'no_node' } }));
      return;
    }
    setApplyState((prev) => ({ ...prev, [msg.id]: { status: 'loading' } }));
    try {
      const result = await requestApplyInstruction(msg.content, { nodeContext: overrideNode });
      if (result?.ok) {
        setApplyState((prev) => ({ ...prev, [msg.id]: { status: 'ok', detail: result.summary || null } }));
        // 🟦 V11.1 + V11.3 — actualizar memoria con el nodo post-patch +
        // patch REAL (RFC 6902) para que Recipe Replay pueda re-aplicarlo.
        const realPatch = Array.isArray(result.patch) ? result.patch : [];
        if (result.updatedNodeContext) {
          rememberPatch({
            instruction: msg.content,
            patch: realPatch,
            updatedNodeContext: result.updatedNodeContext,
            screenshotB64: null,
          });
        } else if (overrideNode) {
          rememberPatch({
            instruction: msg.content,
            patch: realPatch,
            updatedNodeContext: overrideNode,
            screenshotB64: null,
          });
        }
        // Auto-clear el "OK" visual después de 2.5s.
        setTimeout(() => {
          setApplyState((prev) => {
            if (prev[msg.id]?.status !== 'ok') return prev;
            const next = { ...prev };
            delete next[msg.id];
            return next;
          });
        }, 2500);
      } else {
        setApplyState((prev) => ({ ...prev, [msg.id]: { status: 'err', detail: result?.error || 'unknown' } }));
      }
    } catch (e) {
      setApplyState((prev) => ({ ...prev, [msg.id]: { status: 'err', detail: e?.message || 'unknown' } }));
    }
  }, [builderSelection, multiStep]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <>
      {/* Botón en el header (geniecito + burbujitas premium del Cuadro Negro
          2026-02 — versión + grande con 5 burbujas). Tamaño 40×40, padding
          6px, border-radius 12px, fondo transparente con hover #1f1f1f,
          idle pulse 2.8s y 5 burbujas violeta/celeste. Al abrir el modal
          se dispara `genieActivate` via la clase temporal
          `.genie-activate`. */}
      <button
        ref={triggerRef}
        type="button"
        onClick={handleOpen}
        title={es ? 'Ayuda con Lyon IA' : 'Help with Lyon AI'}
        data-testid="genio-open"
        aria-label={es ? 'Ayuda con Lyon IA' : 'Help with Lyon AI'}
        className={`genie-button group h-[44px] w-[44px] p-[7px] rounded-[13px] relative flex items-center justify-center${isActivating ? ' genie-activate' : ''}${open && minimized ? ' lyon-is-minimized' : ''}`}
      >
        <span className="genie-button-inner relative z-10">
          <GenieIcon size={28} />
        </span>
        {/* 7 burbujas (5 del Cuadro Negro + 2 extra solo desktop para
            mayor abundancia visual a pedido del usuario). Las 5 originales
            mantienen posición/color/delay exactos del Cuadro Negro. */}
        <span className="genie-bubble genie-bubble-1" aria-hidden="true" />
        <span className="genie-bubble genie-bubble-2" aria-hidden="true" />
        <span className="genie-bubble genie-bubble-3" aria-hidden="true" />
        <span className="genie-bubble genie-bubble-4" aria-hidden="true" />
        <span className="genie-bubble genie-bubble-5" aria-hidden="true" />
        <span className="genie-bubble genie-bubble-6" aria-hidden="true" />
        <span className="genie-bubble genie-bubble-7" aria-hidden="true" />
      </button>

      {/* CSS de las burbujitas — inyectado una sola vez por el primer botón.
          Keyframes: suben ~28px, escalan ligeramente, desvanecen. */}
      <style>{`
        /* 🟥 CUADRO NEGRO 2026-02 — Microinteracciones premium del botón
            del Genio. Estilos base + idle pulse + 3 burbujas + hover/active
            + genieActivate al abrir el modal. NO TOCAR sin un nuevo
            Cuadro Negro. */
        .genie-button {
          position: relative;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 44px;
          height: 44px;
          padding: 7px;
          border-radius: 13px;
          background-color: transparent;
          color: #f5f5f5;
          overflow: visible;
          transition: transform 160ms ease-out,
                      background-color 160ms ease-out,
                      box-shadow 160ms ease-out,
                      opacity 160ms ease-out;
        }
        /* Desktop bump (≥640px): un poquito más grandecito a pedido del
            usuario (feb-2026, "+2mm"). Mobile queda en 44×44 / radius 13. */
        @media (min-width: 640px) {
          .genie-button {
            width: 48px;
            height: 48px;
            border-radius: 14px;
            padding: 8px;
          }
        }
        .genie-button:hover {
          background-color: #1f1f1f;
          color: #ffffff;
          box-shadow: 0 0 8px rgba(168, 85, 247, 0.25);
          transform: scale(1.06);
        }
        .genie-button:active {
          transform: scale(0.92);
          opacity: 0.85;
        }
        .genie-button-inner {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          animation: genieIdlePulse 2.8s ease-in-out infinite;
          will-change: transform, opacity;
        }
        .genie-button.genie-activate .genie-button-inner {
          animation: genieActivate 260ms ease-out;
        }
        @keyframes genieIdlePulse {
          0%   { transform: scale(1); opacity: 1; }
          50%  { transform: scale(1.04); opacity: 0.92; }
          100% { transform: scale(1); opacity: 1; }
        }
        @keyframes genieActivate {
          0%   { transform: scale(1);    filter: brightness(1); }
          40%  { transform: scale(1.18); filter: brightness(1.4); }
          100% { transform: scale(1);    filter: brightness(1); }
        }
        .genie-bubble {
          position: absolute;
          bottom: 6px;
          left: 50%;
          width: 6px;
          height: 6px;
          border-radius: 9999px;
          pointer-events: none;
          animation: genieBubble 1.8s ease-out infinite;
          will-change: transform, opacity;
        }
        .genie-bubble-1 {
          left: 28%;
          background: #a855f7;
          box-shadow: 0 0 6px rgba(168, 85, 247, 0.8);
          animation-delay: 0.2s;
        }
        .genie-bubble-2 {
          left: 42%;
          background: #38bdf8;
          box-shadow: 0 0 6px rgba(56, 189, 248, 0.8);
          animation-delay: 0.4s;
        }
        .genie-bubble-3 {
          left: 54%;
          background: #a855f7;
          box-shadow: 0 0 6px rgba(168, 85, 247, 0.8);
          animation-delay: 0.6s;
        }
        .genie-bubble-4 {
          left: 68%;
          background: #38bdf8;
          box-shadow: 0 0 6px rgba(56, 189, 248, 0.8);
          animation-delay: 0.9s;
          width: 5px;
          height: 5px;
        }
        .genie-bubble-5 {
          left: 36%;
          background: #c084fc;
          box-shadow: 0 0 5px rgba(192, 132, 252, 0.7);
          animation-delay: 1.1s;
          width: 5px;
          height: 5px;
        }
        .genie-bubble-6 {
          left: 48%;
          background: #7dd3fc;
          box-shadow: 0 0 5px rgba(125, 211, 252, 0.7);
          animation-delay: 0.35s;
          width: 4px;
          height: 4px;
        }
        .genie-bubble-7 {
          left: 60%;
          background: #d8b4fe;
          box-shadow: 0 0 5px rgba(216, 180, 254, 0.7);
          animation-delay: 0.75s;
          width: 4px;
          height: 4px;
        }
        @keyframes genieBubble {
          0%   { transform: translateY(0) scale(0.5); opacity: 0; }
          15%  { opacity: 1; }
          70%  { opacity: 0.85; }
          100% { transform: translateY(-18px) scale(1.2); opacity: 0; }
        }

        .lyon-bubbles { pointer-events: none; }
        .lyon-bubble {
          position: absolute;
          bottom: 0;
          border-radius: 9999px;
          opacity: 0;
          filter: blur(0.3px);
          box-shadow: 0 0 6px currentColor;
          animation-name: lyon-bubble-rise;
          animation-iteration-count: infinite;
          animation-timing-function: ease-in-out;
          will-change: transform, opacity;
        }
        @keyframes lyon-bubble-rise {
          0%   { transform: translateY(4px) scale(0.6); opacity: 0; }
          15%  { opacity: 0.85; }
          70%  { opacity: 0.6; }
          100% { transform: translateY(-22px) scale(1.05); opacity: 0; }
        }
        /* 🟥 Scroll único y visible SOLO para el área de diálogos de Lyon.
            No afecta a otros scrollbars de la app. */
        .lyon-msgs-scroll {
          scrollbar-width: thin;
          scrollbar-color: #6C47FF #15161A;
        }
        .lyon-msgs-scroll::-webkit-scrollbar { width: 8px; }
        .lyon-msgs-scroll::-webkit-scrollbar-track {
          background: #0F1014;
          border-radius: 4px;
        }
        .lyon-msgs-scroll::-webkit-scrollbar-thumb {
          background: linear-gradient(180deg, #7C3AED, #D946EF);
          border-radius: 4px;
          border: 1px solid #1E1F22;
        }
        .lyon-msgs-scroll::-webkit-scrollbar-thumb:hover {
          background: linear-gradient(180deg, #8B5CF6, #E879F9);
        }
        /* Mini-icono flotante: pulso suave para reforzar que es un FAB
            interactivo y arrastrable. Solo aplica al mini, no al panel. */
        .lyon-mini-floating {
          animation: lyon-float 3.5s ease-in-out infinite;
        }
        .lyon-mini-floating:active { animation: none; }
        @keyframes lyon-float {
          0%, 100% { transform: translateY(0); box-shadow: 0 8px 20px -6px rgba(168, 85, 247, 0.55); }
          50%      { transform: translateY(-3px); box-shadow: 0 14px 26px -8px rgba(217, 70, 239, 0.65); }
        }
        /* 🟥 V12.3 — Handles de resize SOLO en bordes (per Cuadro Negro).
           4 zonas absolutas transparentes con highlight púrpura al hover.
           Las esquinas quedan inertes — el panel NO se redimensiona
           diagonalmente. Cubren el 100% del borde sin gap. */
        /* 🟥 FIX feb-2026 — Anillo pulsante violeta alrededor del mini
           para indicar claramente que Lyon está minimizado y se puede
           reabrir clickeándolo. */
        .lyon-mini-ring::before {
          content: '';
          position: absolute;
          inset: -6px;
          border-radius: 9999px;
          border: 2px solid rgba(168, 85, 247, 0.7);
          animation: lyon-mini-ring-pulse 1.8s ease-out infinite;
          pointer-events: none;
        }
        @keyframes lyon-mini-ring-pulse {
          0%   { transform: scale(0.85); opacity: 0.9; }
          70%  { transform: scale(1.25); opacity: 0; }
          100% { transform: scale(1.25); opacity: 0; }
        }
        /* 🟥 FIX feb-2026 — Indicador en el botón del HEADER cuando Lyon
           está minimizado: anillo morado pulsante para que el usuario sepa
           "Lyon está activo en background, click para reabrir". */
        .genie-button.lyon-is-minimized {
          background-color: rgba(124, 58, 237, 0.18);
          box-shadow: 0 0 0 2px rgba(168, 85, 247, 0.6), 0 0 14px rgba(168, 85, 247, 0.5);
          animation: lyon-header-minimized-pulse 2s ease-in-out infinite;
        }
        @keyframes lyon-header-minimized-pulse {
          0%, 100% { box-shadow: 0 0 0 2px rgba(168, 85, 247, 0.45), 0 0 10px rgba(168, 85, 247, 0.35); }
          50%      { box-shadow: 0 0 0 3px rgba(217, 70, 239, 0.75), 0 0 22px rgba(217, 70, 239, 0.55); }
        }
        .dxa-rz-edge {
          position: absolute;
          background: transparent;
          transition: background-color 0.15s ease;
          touch-action: none;
          z-index: 10;
        }
        .dxa-rz-edge:hover {
          background-color: rgba(168, 85, 247, 0.28);
        }
        .dxa-rz-edge:active {
          background-color: rgba(168, 85, 247, 0.45);
        }
        .dxa-rz-n  { top: 0;    left: 8px; right: 8px; height: 6px; cursor: ns-resize; }
        .dxa-rz-s  { bottom: 0; left: 8px; right: 8px; height: 6px; cursor: ns-resize; }
        .dxa-rz-w  { left: 0;   top: 8px;  bottom: 8px; width: 6px; cursor: ew-resize; }
        .dxa-rz-e  { right: 0;  top: 8px;  bottom: 8px; width: 6px; cursor: ew-resize; }
        /* 🟥 feedback usuario jun-2026 — en mobile los bordes de 6px son
           imposibles de tappear con el dedo (necesita ~44px de target).
           Agrandamos a 14px de hit area en mobile manteniendo la línea
           visual fina (el background sigue siendo transparente hasta hover). */
        @media (max-width: 640px) {
          .dxa-rz-n, .dxa-rz-s { height: 14px; }
          .dxa-rz-w, .dxa-rz-e { width: 14px; }
        }
      `}</style>

      {/* Panel flotante movible (sin backdrop modal — interactuable
          con la app detrás). Cuando está minimizado o cerrado, se oculta
          vía display:none — NO se desmonta — para preservar el inline
          style del native CSS resize escrito por el navegador. */}
      {/* 🟥 CUADRO NEGRO 2026-02-26 — createPortal al document.body para que
          el `position: fixed` del panel NO quede contenido por ningún
          ancestor con `transform/filter/will-change` (que en mobile lo
          posicionaba a y:-588 en vez de bottom:0). Renderizar directo al
          body garantiza que `fixed` se ancle al viewport real. */}
      {open && createPortal((
        <div
          className="fixed z-[9999]"
          data-testid="lyon-panel"
          style={{
            ...(position
              ? { left: `${position.x}px`, top: `${position.y}px` }
              : { left: 0, right: 0, bottom: 0 }),
            display: minimized ? 'none' : 'block',
          }}
        >
          {/* 🟥 RESIZABLE PANEL — el contenedor exterior es redimensionable
              nativamente vía CSS `resize: both` desde la esquina inferior
              derecha. Min/Max constrainen el tamaño. `overflow: hidden`
              evita que el contenido escalado se desborde visualmente
              (los scrolls internos se gestionan en el wrapper interno). */}
          <div
            ref={panelRef}
            data-testid="lyon-panel-resizable"
            className={`bg-gradient-to-b from-slate-900 to-slate-950 border border-purple-500/30 rounded-t-2xl sm:rounded-2xl shadow-2xl ${
              isDragging ? 'select-none' : ''
            }`}
            style={{
              // 🟥 Mobile bottom-sheet fix (feb-2026): cuando no hay posición
              // guardada (position === null en mobile), el panel debe usar
              // `position: relative` para que crezca hacia arriba desde el
              // outer `bottom: 0` en vez de extenderse fuera del viewport.
              position: position ? 'absolute' : 'relative',
              resize: 'none',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              minWidth: '320px',
              minHeight: '280px',
              maxWidth: '90vw',
              maxHeight: '90vh',
              touchAction: isDragging ? 'none' : 'auto',
            }}
          >
            {/* 🟦 V12.6 — INNER FLUYE 100% DEL PANEL (per feedback usuario):
                eliminado el transform-scale y el anchor wrapper. Ahora el inner
                ocupa exactamente 100% del panel con flex-column: header arriba,
                mensajes flex-1 (crecen al alto disponible), footer abajo. Sin
                gaps arriba/abajo cuando el panel se agranda. El contenido
                NO hace zoom — fluye naturalmente y aprovecha todo el espacio. */}
            <div
              data-testid="lyon-panel-inner"
              className="flex flex-col"
              style={{
                width: '100%',
                height: '100%',
              }}
            >
            {/* Header del panel — ZONA DE ARRASTRE.
                onPointerDown captura inicio de drag; el listener global
                actualiza la posición mientras el puntero se mueve.
                🟦 CUADRO NEGRO 2026-02-26 — Clase `lyon-header-safe` añadida
                para respetar safe-area-inset-top en iOS/Android, garantizar
                z-index alto y min-height en mobile. */}
            <header
              onPointerDown={handleDragStart}
              data-testid="lyon-panel-header"
              className="lyon-header-safe flex items-center justify-between px-4 py-3 border-b border-slate-800 bg-gradient-to-r from-purple-900/40 to-fuchsia-900/30 cursor-move select-none"
              style={{ touchAction: 'none' }}
            >
              <div className="flex items-center gap-3 pointer-events-none">
                <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-indigo-700 via-purple-700 to-fuchsia-700 flex items-center justify-center shadow">
                  <GenieIcon size={20} />
                </div>
                <div>
                  <h2 className="text-sm font-bold text-white leading-tight">
                    {es ? 'Aqui Lyon , tu Ayuda IA' : 'Here is Lyon, your AI Help'}
                  </h2>
                </div>
              </div>
              <div className="flex items-center gap-1">
                {/* Botón minimizar (–) */}
                <button
                  onPointerDown={(e) => e.stopPropagation()}
                  onClick={() => setMinimized(true)}
                  data-testid="lyon-minimize-btn"
                  className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
                  aria-label={es ? 'Minimizar' : 'Minimize'}
                  title={es ? 'Minimizar' : 'Minimize'}
                >
                  <Minus className="w-5 h-5" />
                </button>
                {/* Botón cerrar (×) — CUADRO NEGRO: pide confirmación antes de cerrar. */}
                <button
                  onPointerDown={(e) => e.stopPropagation()}
                  onClick={() => setShowCloseConfirm(true)}
                  data-testid="lyon-close-btn"
                  className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
                  aria-label={es ? 'Cerrar' : 'Close'}
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </header>

            {/* 🟦 CUADRO NEGRO Navegación Lyon — modal de confirmación de cierre. */}
            {showCloseConfirm && (
              <div
                className="absolute inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
                data-testid="lyon-close-confirm-overlay"
                onClick={(e) => { if (e.target === e.currentTarget) setShowCloseConfirm(false); }}
              >
                <div className="bg-slate-900 border border-slate-700 rounded-xl p-5 max-w-xs mx-4 shadow-2xl" data-testid="lyon-close-confirm-modal">
                  <h3 className="text-sm font-semibold text-white mb-2">
                    {input && input.trim()
                      ? (es ? '¿Querés cerrar Lyon? Tenés cambios sin guardar.' : 'Close Lyon? You have unsaved changes.')
                      : (es ? '¿Querés cerrar Lyon?' : 'Close Lyon?')}
                  </h3>
                  <div className="flex flex-col gap-2 mt-4">
                    {input && input.trim() ? (
                      <>
                        <button
                          type="button"
                          data-testid="lyon-close-save-exit"
                          onClick={() => {
                            handleSend();
                            setShowCloseConfirm(false);
                            setOpen(false);
                          }}
                          className="w-full px-3 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-medium"
                        >
                          {es ? 'Guardar y salir' : 'Save and exit'}
                        </button>
                        <button
                          type="button"
                          data-testid="lyon-close-exit-no-save"
                          onClick={() => {
                            setInput('');
                            setShowCloseConfirm(false);
                            setOpen(false);
                          }}
                          className="w-full px-3 py-2 rounded-lg bg-slate-700 hover:bg-slate-600 text-slate-200 text-sm"
                        >
                          {es ? 'Salir sin guardar' : 'Exit without saving'}
                        </button>
                      </>
                    ) : (
                      <button
                        type="button"
                        data-testid="lyon-close-yes"
                        onClick={() => {
                          setShowCloseConfirm(false);
                          setOpen(false);
                        }}
                        className="w-full px-3 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-medium"
                      >
                        {es ? 'Sí, cerrar' : 'Yes, close'}
                      </button>
                    )}
                    <button
                      type="button"
                      data-testid="lyon-close-cancel"
                      onClick={() => setShowCloseConfirm(false)}
                      className="w-full px-3 py-2 rounded-lg bg-transparent border border-slate-600 hover:bg-slate-800 text-slate-300 text-sm"
                    >
                      {es ? 'Cancelar' : 'Cancel'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Mensajes */}
            <div
              ref={scrollRef}
              className="lyon-msgs-scroll flex-1 min-h-0 overflow-y-auto px-4 pt-3 pb-12 space-y-3"
              data-testid="lyon-messages"
            >
              {messages.length === 0 && (
                <div className="rounded-xl bg-slate-800/50 border border-purple-500/20 p-4">
                  <ul className="space-y-1.5">
                    {bullets.map((b, i) => (
                      <li key={i} className="text-[12px] text-slate-300 flex items-start gap-2">
                        <span className="text-purple-400 mt-[1px]">👉</span>
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {messages.map((m, i) => (
                <div
                  key={m.id || i}
                  className={`group/msg flex flex-col gap-1 ${m.role === 'user' ? 'items-end' : 'items-start'}`}
                  data-testid={`lyon-msg-${m.role}`}
                >
                  {/* Fila: avatar + burbuja (mantiene el layout anterior). */}
                  <div className={`flex gap-2 ${m.role === 'user' ? 'flex-row-reverse' : ''} w-full`}>
                    {m.role === 'user' ? (
                      <div className="shrink-0 w-7 h-7 rounded-full flex items-center justify-center bg-sky-600 text-white">
                        <User className="w-3.5 h-3.5" />
                      </div>
                    ) : (
                      /* Avatar de Lyon: solo el geniecito, sin cuadrito de fondo
                         (estilo similar al botón GitHub del header). Lleva
                         burbujitas saliendo hacia arriba, marca de Lyon IA. */
                      <div className="shrink-0 relative w-7 h-7 flex items-center justify-center overflow-visible" data-testid="lyon-msg-avatar">
                        <GenieIcon size={22} />
                        <span className="lyon-bubbles pointer-events-none absolute inset-x-0 -top-1 h-5 overflow-visible" aria-hidden="true">
                          {BUBBLES.map((b, i) => (
                            <span
                              key={i}
                              className="lyon-bubble"
                              style={{
                                left: b.left,
                                width: `${Math.max(2, b.size - 1)}px`,
                                height: `${Math.max(2, b.size - 1)}px`,
                                background: b.color,
                                animationDelay: b.delay,
                                animationDuration: b.duration,
                              }}
                            />
                          ))}
                        </span>
                      </div>
                    )}
                    <div
                      className={`max-w-[78%] rounded-2xl px-3 py-2 text-[13px] whitespace-pre-wrap leading-snug ${
                        m.role === 'user'
                          ? 'bg-sky-600 text-white rounded-tr-sm'
                          : m.error
                            ? 'bg-red-900/40 text-red-200 border border-red-500/30 rounded-tl-sm'
                            : 'bg-slate-800 text-slate-100 border border-slate-700 rounded-tl-sm'
                      }`}
                    >
                      {m.content}
                    </div>
                  </div>
                  {/* Acciones DEBAJO de la burbuja, alineadas a la derecha y
                      siempre visibles. No alteran tamaño ni layout. */}
                  {m.id && (
                    <div className="relative flex items-center gap-1 pr-9">
                      <button
                        type="button"
                        onClick={() => handleCopyMessage(m)}
                        data-testid={`lyon-msg-copy-${m.id}`}
                        aria-label={es ? 'Copiar mensaje' : 'Copy message'}
                        title={copiedId === m.id ? (es ? 'Copiado' : 'Copied') : (es ? 'Copiar' : 'Copy')}
                        className="p-1 rounded-md bg-slate-800/60 hover:bg-slate-700 text-slate-300 hover:text-white transition-colors border border-slate-700/70"
                      >
                        {copiedId === m.id
                          ? <Check className="w-3.5 h-3.5 text-emerald-400" />
                          : <Copy className="w-3.5 h-3.5" />}
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDeleteMessage(m.id)}
                        data-testid={`lyon-msg-delete-${m.id}`}
                        aria-label={es ? 'Borrar mensaje' : 'Delete message'}
                        title={es ? 'Borrar' : 'Delete'}
                        className="p-1 rounded-md bg-slate-800/60 hover:bg-red-900/60 text-slate-300 hover:text-red-300 transition-colors border border-slate-700/70"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                      {/* 🟦 V11.0 Phase 7+ — Botón "Aplicar como patch" SOLO para
                          mensajes del asistente, no-error, y cuando hay un
                          nodo seleccionado en el Builder O hay memoria
                          multi-paso disponible (V11.1). */}
                      {m.role === 'assistant' && !m.error && (builderSelection || (multiStep && memory.lastNodeId)) && (
                        (() => {
                          const st = applyState[m.id];
                          const isLoading = st?.status === 'loading';
                          const isOk = st?.status === 'ok';
                          const isErr = st?.status === 'err';
                          return (
                            <button
                              type="button"
                              onClick={() => handleApplyAsPatch(m)}
                              disabled={isLoading}
                              data-testid={`lyon-msg-apply-${m.id}`}
                              aria-label={es ? 'Aplicar como patch al nodo' : 'Apply as patch to node'}
                              title={es
                                ? 'Pedir a la IA que aplique esta sugerencia al nodo seleccionado'
                                : 'Ask the AI to apply this suggestion to the selected node'}
                              className={`p-1 rounded-md border transition-colors flex items-center gap-1 text-[10px] font-semibold ${
                                isLoading
                                  ? 'bg-fuchsia-900/40 text-fuchsia-200 border-fuchsia-500/40 cursor-wait'
                                  : isOk
                                    ? 'bg-emerald-900/40 text-emerald-200 border-emerald-500/40'
                                    : isErr
                                      ? 'bg-red-900/40 text-red-200 border-red-500/40'
                                      : 'bg-purple-900/50 hover:bg-purple-800/60 text-purple-200 hover:text-white border-purple-500/40'
                              }`}
                            >
                              {isLoading ? (
                                <>
                                  <Loader2 className="w-3 h-3 animate-spin" />
                                  <span>{es ? 'Aplicando' : 'Applying'}</span>
                                </>
                              ) : isOk ? (
                                <>
                                  <CheckCircle2 className="w-3 h-3" />
                                  <span>{es ? 'Aplicado' : 'Applied'}</span>
                                </>
                              ) : isErr ? (
                                <>
                                  <AlertTriangle className="w-3 h-3" />
                                  <span data-testid={`lyon-msg-apply-error-${m.id}`}>{es ? 'Error' : 'Error'}</span>
                                </>
                              ) : (
                                <>
                                  <Wand2 className="w-3 h-3" />
                                  <span>{es ? 'Aplicar como patch' : 'Apply as patch'}</span>
                                </>
                              )}
                            </button>
                          );
                        })()
                      )}
                      {copiedId === m.id && (
                        <span
                          className="text-[10px] px-1.5 py-0.5 rounded-md bg-emerald-600/90 text-white shadow ml-1"
                          data-testid={`lyon-msg-copied-${m.id}`}
                        >
                          {es ? 'Copiado' : 'Copied'}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              ))}

              {sending && (
                <div className="flex gap-2" data-testid="lyon-typing">
                  <div className="shrink-0 relative w-7 h-7 flex items-center justify-center overflow-visible">
                    <GenieIcon size={22} />
                    <span className="lyon-bubbles pointer-events-none absolute inset-x-0 -top-1 h-5 overflow-visible" aria-hidden="true">
                      {BUBBLES.map((b, i) => (
                        <span
                          key={i}
                          className="lyon-bubble"
                          style={{
                            left: b.left,
                            width: `${Math.max(2, b.size - 1)}px`,
                            height: `${Math.max(2, b.size - 1)}px`,
                            background: b.color,
                            animationDelay: b.delay,
                            animationDuration: b.duration,
                          }}
                        />
                      ))}
                    </span>
                  </div>
                  <div className="rounded-2xl rounded-tl-sm px-3 py-2 bg-slate-800 border border-slate-700">
                    <Loader2 className="w-4 h-4 text-purple-400 animate-spin" />
                  </div>
                </div>
              )}
            </div>

            {/* 🟦 V11.0 Phase 7 — Chip de contexto del nodo del UI Builder.
                Visible SOLO cuando hay una selección activa publicada por
                el Builder. Permite al usuario decidir si Lyon recibe el
                contexto del nodo y, opcionalmente, un screenshot (Vision).
                🟦 V11.1 — También se muestra (modo "memoria") cuando el
                multi-step está ON y hay un nodo recordado pero no hay
                selección actual — Lyon va a usar la memoria en el próximo
                mensaje referencial. */}
            {(builderSelection || (multiStep && memory.lastNodeId)) && (
              <div
                data-testid="lyon-builder-context-chip"
                className="px-3 py-2 border-t border-purple-500/20 bg-gradient-to-r from-purple-950/40 to-fuchsia-950/30"
              >
                <div className="flex items-start gap-2">
                  <Layers className={`w-3.5 h-3.5 mt-0.5 shrink-0 ${builderSelection ? 'text-purple-300' : 'text-fuchsia-400'}`} />
                  <div className="flex-1 min-w-0">
                    <div className="text-[11px] font-semibold text-purple-200 leading-tight">
                      {builderSelection
                        ? (es ? 'Nodo activo en el UI Builder' : 'Active node in UI Builder')
                        : (es ? 'Nodo recordado (memoria multi-paso)' : 'Remembered node (multi-step memory)')}
                    </div>
                    <div
                      className="text-[10px] text-slate-400 truncate font-mono"
                      title={`<${(builderSelection || memory.lastNodeContext)?.tag || 'div'}> ${(builderSelection || memory.lastNodeContext)?.className || ''}`}
                      data-testid="lyon-builder-context-label"
                    >
                      &lt;{(builderSelection || memory.lastNodeContext)?.tag || 'div'}&gt;
                      {(() => {
                        const cn = (builderSelection || memory.lastNodeContext)?.className || '';
                        return cn ? ` .${cn.split(/\s+/).slice(0, 2).join('.')}` : '';
                      })()}
                      {memory.lastPatches.length > 0 && !builderSelection && (
                        <span className="ml-2 text-fuchsia-400">· {memory.lastPatches.length} patch(es) aplicados</span>
                      )}
                    </div>
                  </div>
                </div>
                {builderSelection && (
                  <div className="mt-2 flex items-center gap-3 flex-wrap">
                    <label className="flex items-center gap-1.5 text-[11px] text-slate-300 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        checked={attachContext}
                        onChange={(e) => setAttachContext(e.target.checked)}
                        data-testid="lyon-toggle-attach-context"
                        className="w-3 h-3 accent-purple-500"
                      />
                      {es ? 'Adjuntar contexto' : 'Attach context'}
                    </label>
                    <label
                      className={`flex items-center gap-1.5 text-[11px] cursor-pointer select-none ${attachContext ? 'text-slate-300' : 'text-slate-600 cursor-not-allowed'}`}
                      title={es
                        ? 'Captura un screenshot del nodo con html2canvas para que Lyon lo vea (Vision)'
                        : 'Capture a node screenshot with html2canvas so Lyon can see it (Vision)'}
                    >
                      <input
                        type="checkbox"
                        checked={attachVision}
                        disabled={!attachContext}
                        onChange={(e) => setAttachVision(e.target.checked)}
                        data-testid="lyon-toggle-attach-vision"
                        className="w-3 h-3 accent-fuchsia-500"
                      />
                      <ImageIcon className="w-3 h-3" />
                      {es ? 'Adjuntar vista (Vision)' : 'Attach view (Vision)'}
                      {capturingShot && <Loader2 className="w-3 h-3 animate-spin text-fuchsia-300" />}
                    </label>
                  </div>
                )}
              </div>
            )}

            {/* 🟦 V11.1 — Toggle "Conversación multi-paso". Habilita el uso
                automático de la memoria de Lyon cuando el texto del usuario
                contiene referencias implícitas ("ese mismo", "ahora hacelo
                más X", "continuá"). Independiente de attachContext: éste
                actúa cuando NO hay selección manual.
                🟦 V11.3 — En la misma fila agregamos los botones de Recipe
                Replay (guardar receta, abrir panel de recetas). */}
            <div
              data-testid="lyon-multistep-row"
              className="px-3 py-1.5 border-t border-slate-800 bg-slate-900/50 flex items-center gap-2"
            >
              <label className="shrink-0 flex items-center gap-1.5 text-[11px] text-slate-300 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={multiStep}
                  onChange={(e) => setMultiStep(e.target.checked)}
                  data-testid="lyon-toggle-multistep"
                  className="w-3 h-3 accent-fuchsia-500"
                />
                <GitBranch className="w-3 h-3 text-fuchsia-300" />
                {es ? 'Conversación multi-paso' : 'Multi-step conversation'}
              </label>
              {/* 🟥 Scroll horizontal feb-2026 — el toolbar es flex-1 + min-w-0
                  para poder hacer shrink y mostrar scrollbar interno cuando
                  los 7 botones no entran en el ancho actual del Genio. */}
              <div className="flex-1 min-w-0">
              <LyonHeaderToolbar
                es={es}
                multiStep={multiStep}
                canSaveRecipe={canSaveRecipe}
                hasAppContext={Boolean(currentAppId)}
                inferenceLoading={inferenceLoading}
                importBusy={importBusy}
                recipes={recipes}
                memory={memory}
                onSaveRecipe={() => withPremiumGate(() => handleOpenSaveRecipe())}
                onInferStyle={() => withPremiumGate(() => { runGenioCommand('infer-style'); handleStartInference(); })}
                onOpenRecipes={() => withPremiumGate(() => { runGenioCommand('open-recipes'); setMinimized(true); setRecipesPanelOpen(true); })}
                onOpenMarketplace={() => withPremiumGate(() => { runGenioCommand('open-marketplace'); setMinimized(true); setMarketplaceOpen(true); })}
                onOpenProyectos={() => { runGenioCommand('open-proyectos'); setMinimized(true); setProyectosOpen(true); }}
                onOpenStylePacks={() => withPremiumGate(() => { runGenioCommand('open-stylepacks'); setMinimized(true); setStylePacksOpen(true); })}
                onOpenOnboarding={() => {
                  // 🟢 Onboarding es AYUDA, no modifica → accesible para Gratis + Premium.
                  runGenioCommand('open-onboarding');
                  setMinimized(true);
                  if (typeof window !== 'undefined' && typeof window.__damyxia_openOnboarding === 'function') {
                    window.__damyxia_openOnboarding();
                  }
                }}
                onImportRecipe={() => withPremiumGate(() => { runGenioCommand('open-import'); handleImportRecipeFromFile(); })}
              />
              </div>
            </div>

            {/* 🟥 Fase 2 — Verb chips (entrada visual de Auto-Visual Creation) */}
            <VerbChipsRow
              disabled={sending}
              onSelectPrompt={(prompt) => {
                setInput(prompt);
                // Focus + poner el cursor al final del textarea.
                setTimeout(() => {
                  const ta = document.querySelector('[data-testid="lyon-input"]');
                  if (ta) {
                    ta.focus();
                    try { ta.setSelectionRange(prompt.length, prompt.length); } catch (_) {}
                  }
                }, 0);
              }}
            />

            {/* Input */}
            <div className="px-3 py-3 border-t border-slate-800 bg-slate-900/60">
              <div className="flex items-end gap-2">
                <textarea
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={es ? 'Pregúntale a Lyon...' : 'Ask Lyon...'}
                  rows={1}
                  className="flex-1 resize-none rounded-xl bg-slate-800 border border-slate-700 focus:border-purple-500 focus:outline-none px-3 py-2 text-[13px] text-white placeholder-slate-500 max-h-32"
                  data-testid="lyon-input"
                  disabled={sending}
                />
                <button
                  onClick={handleSend}
                  disabled={sending || !input.trim()}
                  data-testid="lyon-send-btn"
                  className="shrink-0 w-9 h-9 rounded-xl bg-gradient-to-br from-purple-600 to-fuchsia-600 hover:from-purple-500 hover:to-fuchsia-500 disabled:opacity-40 disabled:cursor-not-allowed text-white flex items-center justify-center transition-all"
                  title={es ? 'Enviar' : 'Send'}
                  aria-label={es ? 'Enviar' : 'Send'}
                >
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </button>
              </div>
              <p className="text-[10px] text-slate-500 mt-1.5 text-center">
                {es
                  ? 'Lyon puede equivocarse. Verifica datos críticos.'
                  : 'Lyon can make mistakes. Verify critical info.'}
              </p>
            </div>
            {/* /lyon-panel-inner (fluye 100% del panel, sin transform) */}

            {/* 🟥 8 RESIZE HANDLES estilo Figma/VS Code — 4 bordes
                (invisibles, brillan al hover) + 4 esquinas (cuadritos
                púrpura visibles, escalan al hover). Cursor nativo del
                navegador. Cada handle dispara `startResize(e, dir)` que
                muta el DOM directamente sin React state → cero loops.
                pointerdown captura el puntero para tracking fuera de
                bounds; pointerup libera. */}
            <div className="dxa-rz-edge dxa-rz-n"  data-testid="lyon-rz-n"  onPointerDown={(e) => startResize(e, 'n')}  />
            <div className="dxa-rz-edge dxa-rz-s"  data-testid="lyon-rz-s"  onPointerDown={(e) => startResize(e, 's')}  />
            <div className="dxa-rz-edge dxa-rz-w"  data-testid="lyon-rz-w"  onPointerDown={(e) => startResize(e, 'w')}  />
            <div className="dxa-rz-edge dxa-rz-e"  data-testid="lyon-rz-e"  onPointerDown={(e) => startResize(e, 'e')}  />
          </div>
          {/* /lyon-panel-resizable */}
        </div>
        {/* /outer positioning wrapper (fixed z-[9999]) */}
        </div>
      ), document.body)}

      {/* Mini-ícono flotante del geniecito — arrastrable.
          Visible SOLO cuando el panel está abierto pero minimizado.
          🟥 FIX feb-2026 — Portal a document.body + ubicación bottom-right
          predecible (estilo widget de chat) + anillo pulsante violeta para
          que sea IMPOSIBLE de no ver. Tamaño 56px (era 48px). */}
      {open && minimized && createPortal((
        <button
          ref={miniBtnRef}
          type="button"
          onPointerDown={handleMiniPointerDown}
          onClick={handleMiniClick}
          data-testid="lyon-mini-btn"
          aria-label={es ? 'Restaurar Lyon' : 'Restore Lyon'}
          title={es ? 'Lyon está minimizado — clic para abrir o arrastrar' : 'Lyon is minimized — click to open or drag'}
          className={`fixed z-[9999] w-14 h-14 rounded-full bg-gradient-to-br from-indigo-700 via-purple-700 to-fuchsia-700 hover:from-indigo-600 hover:to-fuchsia-600 shadow-2xl shadow-purple-500/50 flex items-center justify-center border-2 border-purple-300/70 transition-shadow ${miniDragging ? 'cursor-grabbing scale-105' : 'cursor-grab hover:scale-110'} lyon-mini-floating lyon-mini-ring`}
          style={
            miniPos
              ? { left: `${miniPos.x}px`, top: `${miniPos.y}px`, touchAction: 'none' }
              : { right: '24px', bottom: '24px', touchAction: 'none' }
          }
        >
          <GenieIcon size={28} />
          {/* Punto rojo de "notificación" arriba a la derecha para reforzar visibilidad. */}
          <span
            className="absolute top-0 right-0 w-3 h-3 rounded-full bg-fuchsia-400 border-2 border-slate-900"
            style={{ boxShadow: '0 0 6px #d946ef' }}
            aria-hidden="true"
          />
          {/* Burbujitas alrededor del mini-icono para identidad visual. */}
          <span className="lyon-bubbles pointer-events-none absolute inset-x-0 -top-1 h-7 overflow-visible" aria-hidden="true">
            {BUBBLES.map((b, i) => (
              <span
                key={i}
                className="lyon-bubble"
                style={{
                  left: b.left,
                  width: `${b.size}px`,
                  height: `${b.size}px`,
                  background: b.color,
                  animationDelay: b.delay,
                  animationDuration: b.duration,
                }}
              />
            ))}
          </span>
        </button>
      ), document.body)}

      {/* 🟦 V11.3 — Modal "Guardar receta" (componente extraído) */}
      <LyonSaveRecipeModal
        open={saveRecipeModalOpen}
        onClose={() => setSaveRecipeModalOpen(false)}
        onConfirm={handleConfirmSaveRecipe}
        recipeName={recipeName}
        setRecipeName={(val) => { setRecipeName(val); setRecipeError(null); }}
        recipeError={recipeError}
        memory={memory}
        es={es}
      />

      {/* 🟦 V11.3 — Panel de Recetas (componente extraído) */}
      <LyonRecipesPanel
        open={recipesPanelOpen}
        onClose={() => setRecipesPanelOpen(false)}
        recipes={recipes}
        recipeApplyState={recipeApplyState}
        onApply={handleApplyRecipe}
        onExport={handleExportRecipe}
        onShare={handleShareRecipe}
        onDelete={handleDeleteRecipe}
        es={es}
      />
      {/* 🟦 V11.4 — Modal de Style Inference */}
      <StyleInferenceModal
        open={inferenceModalOpen}
        result={inferenceResult}
        busy={inferenceLoading}
        onClose={() => { setInferenceModalOpen(false); setInferenceResult(null); }}
        onSaveRecipe={handleSaveInferredAsRecipe}
        onApplyNow={handleApplyInferredNow}
      />
      {/* 🟦 V11.5 — Marketplace de Recetas */}
      {/* 🟦 CUADRO NEGRO — "no se rompe nunca": ErrorBoundary blinda el panel. */}
      <SentryErrorBoundary name="MarketplacePanel">
        <MarketplacePanel
          open={marketplaceOpen}
          onClose={() => setMarketplaceOpen(false)}
          appId={currentAppId}
          es={es}
        />
      </SentryErrorBoundary>
      {/* 🟦 CUADRO NEGRO — Panel "Mis Proyectos del Builder". */}
      <LyonProyectosPanel
        open={proyectosOpen}
        onClose={() => setProyectosOpen(false)}
        es={es}
      />
      {/* 🟦 V12.0 — Style Packs Panel */}
      <LyonStylePacksPanel
        open={stylePacksOpen}
        onClose={() => setStylePacksOpen(false)}
        es={es}
        currentTheme={typeof window !== 'undefined' && typeof window.__damyxia_getCurrentTheme === 'function' ? window.__damyxia_getCurrentTheme() : null}
        onApplyPack={async (pack) => {
          // 🟥 CUADRO NEGRO FIX STYLE PACKS (feb-2026) — Cuando estamos
          // DENTRO del Builder, delegamos a `window.__damyxia_applyStylePack`
          // (lo registra UiBuilderPage) que aplica theme + effects + recipes
          // sobre el iframe del proyecto. Cuando estamos FUERA del Builder
          // (home, dashboard, historia, etc.) ese bridge no existe — antes
          // devolvíamos `no_bridge` y la card mostraba "Error". Ahora hay
          // fallback: aplicamos el theme directamente como CSS vars sobre
          // <html> para que el usuario vea cambios visibles incluso fuera
          // del Builder. No aplica recipes (requieren iframe).
          try {
            if (typeof window !== 'undefined' && typeof window.__damyxia_applyStylePack === 'function') {
              return await window.__damyxia_applyStylePack(pack);
            }
            // ── Fallback global ──
            if (!pack || !pack.theme) return { ok: false, error: 'no_theme' };
            const root = typeof document !== 'undefined' ? document.documentElement : null;
            if (!root) return { ok: false, error: 'no_dom' };
            const t = pack.theme || {};
            const setVar = (k, v) => { if (v != null) root.style.setProperty(k, String(v)); };
            // Colors
            const c = t.colors || {};
            ['primary','secondary','background','surface','text','border','accent','danger','warning','success'].forEach((key) => {
              if (c[key]) setVar(`--damyxia-color-${key}`, c[key]);
            });
            // Radii
            const r = t.radii || {};
            ['xs','sm','md','lg','xl','full'].forEach((k) => { if (r[k] != null) setVar(`--damyxia-radius-${k}`, typeof r[k] === 'number' ? `${r[k]}px` : r[k]); });
            // Shadows
            const sh = t.shadows || {};
            Object.entries(sh).forEach(([k, v]) => { if (v) setVar(`--damyxia-shadow-${k}`, v); });
            // Persistencia best-effort
            try { localStorage.setItem('damyxia_active_stylepack', JSON.stringify({ id: pack.id, version: pack.version || '1.0.0', appliedAt: Date.now() })); } catch (_) { /* noop */ }
            return { ok: true, applied: { theme: true, effects: 0, recipes: 0 }, fallback: 'global' };
          } catch (e) {
            return { ok: false, error: e?.message || 'apply_error' };
          }
        }}
      />
      {/* 🟥 CUADRO NEGRO Genio Premium Gate (feb-2026) — modal mostrado
          cuando un usuario NO Premium intenta abrir el Genio o cuando el
          backend devuelve 403 premium_required desde /api/lyon/*. */}
      <GenioPremiumGateModal
        isOpen={premiumGateOpen}
        onClose={() => setPremiumGateOpen(false)}
      />
    </>
  );
};

export default LyonHeaderButton;
